import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
      // The backend returns 200 OK regardless of whether the email exists
      // (anti-enumeration). We always show the same confirmation screen.
      setSubmitted(true);
    } catch (err) {
      // 429 (rate limit) is the only meaningful error we want to surface
      if (err.response?.status === 429) {
        setError(
          "Has solicitado demasiados restablecimientos. Intenta de nuevo en un minuto."
        );
      } else {
        setError(
          err.response?.data?.detail ||
            "No pudimos procesar tu solicitud. Intenta de nuevo."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-950 p-8"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/notalia-icon.png"
            alt="Notalia"
            className="h-10 mx-auto mb-6 brightness-0 invert"
          />
          <h2
            className="text-2xl font-bold text-white"
            data-testid="forgot-password-heading"
          >
            ¿Olvidaste tu contraseña?
          </h2>
          <p className="text-slate-400 mt-2 text-sm">
            Ingresa tu correo y te enviaremos un enlace para crear una nueva.
          </p>
        </div>

        {submitted ? (
          <div
            className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-5 py-6 rounded-lg text-center space-y-3"
            data-testid="forgot-password-success"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto" />
            <p className="text-sm leading-relaxed">
              Si <span className="font-mono">{email}</span> está registrado,
              recibirás un correo con instrucciones para restablecer tu
              contraseña. El enlace expira en 60 minutos.
            </p>
            <p className="text-xs text-emerald-300/70">
              Revisa también tu carpeta de spam.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm"
                data-testid="forgot-password-error"
              >
                {error}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="forgot-password-form"
            >
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    autoFocus
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20 pl-10"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-teal-600 hover:bg-teal-500 text-white font-medium h-11"
                data-testid="forgot-password-submit-button"
              >
                {loading ? "Enviando..." : "Enviar enlace de restablecimiento"}
              </Button>
            </form>
          </>
        )}

        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-300 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio de sesión
        </Link>
      </div>
    </div>
  );
}
